import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Modal } from 'modules/Core/Common/Modal';
import { ModalHeader } from 'modules/Core/Common/Modal/ModalHeader';
import { Label } from 'modules/Core/Common/Forms';
import { Button } from 'modules/Core/Common/Button';
import { Loader } from 'modules/Core/Common/Loader';
import { Toast } from 'modules/Core/Common/Toast';
import { Icon } from 'modules/Core/Common/Icon';
import { MUICheckbox } from 'modules/Core/Common/MUICheckbox';
import { copyToClipBoard } from 'modules/Utils/CopyToClipboard';
import { device } from 'modules/Utils/Devices';
import { Async, Actions, Selectors } from 'modules/Auth/AuthSlice';

const Body = styled.div`
  padding: 16px 20px;
  margin-top: 70px;
  /* dvh follows the visible viewport, so the body doesn't overflow the
     address-bar-adjusted height on mobile Safari (keeps header/footer in view).
     Plain-vh declaration first as a fallback for engines without dvh. */
  max-height: calc(90vh - 80px);
  max-height: calc(min(90vh, 90dvh) - 80px);
  overflow-y: auto;

  > * {
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }

  &::-webkit-scrollbar {
    background: transparent;
    width: 5px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${(props) => props.theme.config.colors.secondary_100};
    border-radius: 20px;
    width: 5px;
  }
`;

const FormGroup = styled.div`
  width: 100%;
  margin-bottom: 8px;
  text-align: center;
`;

const QRWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;

  /* The QR SVG is injected into this div; cap the wrapper (its containing block)
     so a large intrinsic SVG width can't overflow the modal on narrow screens. */
  > div {
    max-width: 100%;
  }

  svg {
    max-width: 100%;
    height: auto;
    image-rendering: pixelated;
  }
`;

const ManualEntry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 16px;
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${(props) => props.theme?.config?.colors?.text_base || '#666'};

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${(props) => props.theme?.config?.colors?.gray_300 || '#ccc'};
  }
`;

const CodeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  code {
    flex: 1;
    min-width: 0;
    padding: 10px 12px;
    border: 1px solid
      ${(props) => props.theme?.config?.colors?.gray_300 || '#ccc'};
    border-radius: 6px;
    background: ${(props) => props.theme?.config?.colors?.light || '#fff'};
    font-family: monospace;
    font-size: 14px;
    letter-spacing: 0;
    text-align: center;
    overflow-x: auto;
    white-space: nowrap;
    color: ${(props) => props.theme?.config?.colors?.text_base || '#333'};
  }

  button svg {
    margin-right: 6px;
  }

  @media (max-width: 500px) {
    code {
      flex: 1 1 100%;
    }

    button {
      display: none;
    }
  }
`;

const Hint = styled.p`
  font-size: 14px;
  text-align: center;
  color: ${(props) => props.theme?.config?.colors?.text_base || '#666'};
  margin: 0;
`;

const Step = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${(props) => props.$gap || '8px'};
  width: 100%;
  max-width: 400px;
`;

const LoadingWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 32px 0;
`;

const SetupForm = styled.form`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  label {
    display: block;
    width: 100%;
    text-align: center;
  }
`;

// Lighter primary while disabled instead of the shared button's gray.
const SubmitButton = styled(Button)`
  &:disabled,
  &:disabled:hover,
  &:disabled:focus,
  &:disabled:active {
    background-color: ${(props) => props.theme.config.colors.primary_300};
    border-color: ${(props) => props.theme.config.colors.primary_300};
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 12px;
  width: 100%;
`;

const CODE_LENGTH = 6;

const OtpWrapper = styled.div`
  display: flex;
  gap: 8px;
  justify-content: center;
  width: 100%;

  @media ${device.mobile} {
    gap: 6px;
  }

  input {
    flex: 1 1 0;
    min-width: 0;
    max-width: 48px;
    height: 52px;
    text-align: center;
    font-size: 22px;
    font-weight: 600;
    color: ${(props) => props.theme?.config?.colors?.text_base || '#333'};
    border: 1px solid
      ${(props) => props.theme?.config?.colors?.gray_300 || '#ccc'};
    border-radius: 6px;
    outline: none;
    transition: border-color 0.2s;

    &:focus {
      border-color: ${(props) =>
        props.theme?.config?.colors?.primary || '#005da3'};
    }
  }
`;

// 6 single-digit boxes; joins to one string and reports via onChange.
// ponytail: sequential left-to-right entry, no middle gaps.
const OtpInput = ({ value, onChange, disabled }) => {
  const refs = useRef([]);
  const chars = Array.from({ length: CODE_LENGTH }, (_, i) => value[i] || '');

  const focusBox = (i) => refs.current[i] && refs.current[i].focus();

  const handleChange = (i, e) => {
    const digit = e.target.value.replace(/\D/g, '').slice(-1);
    const next = [...chars];
    next[i] = digit;
    onChange(next.join(''));
    if (digit && i < CODE_LENGTH - 1) focusBox(i + 1);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !chars[i] && i > 0) {
      e.preventDefault();
      const next = [...chars];
      next[i - 1] = '';
      onChange(next.join(''));
      focusBox(i - 1);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData('text')
      .replace(/\D/g, '')
      .slice(0, CODE_LENGTH);
    if (pasted) {
      onChange(pasted);
      focusBox(Math.min(pasted.length, CODE_LENGTH - 1));
    }
  };

  return (
    <OtpWrapper>
      {chars.map((c, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          value={c}
          disabled={disabled}
          data-test={`totp-digit-${i}`}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
        />
      ))}
    </OtpWrapper>
  );
};

OtpInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

// mode: 'login' (default) — uses Redux twoFactor state + partialToken
// mode: 'setup' — controlled from outside: setupData + onConfirm props
const TwoFactorModal = ({
  isOpen,
  onClose,
  mode,
  setupData: setupDataProp,
  onConfirm,
  onSuccess,
  zIndex,
  methodOptions,
  forcedMethod,
}) => {
  const dispatch = useDispatch();
  const loading = useSelector(Selectors.selectLoading);
  const twoFactor = useSelector(Selectors.twoFactor);

  const [code, setCode] = useState('');
  const [setupData, setSetupData] = useState(null);
  const [loadingSetup, setLoadingSetup] = useState(false);
  const [rememberDevice, setRememberDevice] = useState(false);
  const [chosenMethod, setChosenMethod] = useState(null);
  const [emailSent, setEmailSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(false);

  const isProfileMode = mode === 'setup';
  const isSetup = isProfileMode || twoFactor?.step === 'setup';
  const partialToken = twoFactor?.partialToken;
  const availableMethods = isProfileMode
    ? methodOptions || []
    : twoFactor?.availableMethods || [];
  const loginMethod = twoFactor?.method;
  const activeMethod = isProfileMode
    ? forcedMethod
    : chosenMethod ||
      loginMethod ||
      (isSetup && availableMethods.length === 1 ? availableMethods[0] : null);
  const hasNoMethodAvailable =
    isSetup && !isProfileMode && availableMethods.length === 0;
  const needsMethodPicker =
    isSetup && !isProfileMode && availableMethods.length > 1 && !chosenMethod;

  useEffect(() => {
    if (isProfileMode && setupDataProp) {
      setSetupData(setupDataProp);
      return;
    }
    if (activeMethod !== 'totp') return;
    if (isOpen && isSetup && partialToken && !setupData) {
      setLoadingSetup(true);
      dispatch(
        Async.twoFactorGetSetup(partialToken, (e) => {
          Toast(e.message, 'error');
          setLoadingSetup(false);
        })
      ).then((data) => {
        if (data) setSetupData(data);
        setLoadingSetup(false);
      });
    }
  }, [isOpen, isSetup, partialToken, setupDataProp, activeMethod]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!code.trim()) return;

    if (isProfileMode) {
      onConfirm && onConfirm(code, activeMethod);
      return;
    }

    if (isSetup) {
      if (activeMethod === 'email') {
        dispatch(
          Async.twoFactorVerifyEmail(
            partialToken,
            code,
            false,
            (e) => Toast(e.message, 'error'),
            onSuccess
          )
        );
      } else {
        dispatch(
          Async.twoFactorConfirmSetup(partialToken, code, rememberDevice, (e) =>
            Toast(e.message, 'error')
          )
        );
      }
    } else if (loginMethod === 'email') {
      dispatch(
        Async.twoFactorVerifyEmail(
          partialToken,
          code,
          rememberDevice,
          (e) => Toast(e.message, 'error'),
          onSuccess
        )
      );
    } else {
      dispatch(
        Async.twoFactorValidate(
          partialToken,
          code,
          rememberDevice,
          (e) => Toast(e.message, 'error'),
          onSuccess
        )
      );
    }
  };

  const handleClose = () => {
    // Closing mid-verify would clear twoFactor while loading is still true,
    // which puts the app behind the global spinner with no way back (there is
    // no request timeout to release it). The submit button is already disabled
    // while loading — the close button has to follow the same rule.
    if (loading && !isProfileMode) return;

    if (!isProfileMode) dispatch(Actions.clearTwoFactor());
    setCode('');
    setSetupData(null);
    setRememberDevice(false);
    setChosenMethod(null);
    setEmailSent(false);
    setResendCooldown(false);
    onClose && onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      onCloseModal={handleClose}
      offCloseOnOverlayClick
      // Fluid width: fits narrow viewports (e.g. 320px) without horizontal
      // overflow, capped at 460px on larger screens.
      width="min(92vw, 460px)"
      maxHeight="min(90vh, 90dvh)"
      zIndex={zIndex}
    >
      <div>
        <ModalHeader
          title={
            isSetup
              ? 'Set up Two-Factor Authentication'
              : 'Two-Factor Authentication'
          }
          titleSize="quartenary"
          btnCloseClick={handleClose}
          noHasSubmitButton
        />
        <Body>
          {hasNoMethodAvailable ? (
            <Hint>
              Two-factor authentication is required for your account, but no
              verification method is enabled. Please contact your administrator.
            </Hint>
          ) : needsMethodPicker ? (
            <Step $gap="12px">
              <Hint>
                Choose how you want to receive your verification codes.
              </Hint>
              <Button large onClick={() => setChosenMethod('totp')}>
                Authenticator App
              </Button>
              <Button large outlined onClick={() => setChosenMethod('email')}>
                Email
              </Button>
            </Step>
          ) : isSetup ? (
            <Step>
              {activeMethod === 'email' ? (
                <SetupForm onSubmit={handleSubmit}>
                  <Hint>
                    {emailSent
                      ? 'Enter the 6-digit code we emailed you. It expires in 10 minutes.'
                      : 'We will send a 6-digit code to your email.'}
                  </Hint>
                  {!emailSent ? (
                    <SubmitButton
                      large
                      type="button"
                      disabled={loading}
                      onClick={() => {
                        dispatch(
                          isProfileMode
                            ? Async.profileSendEmail2FA({
                                onSuccess: () => setEmailSent(true),
                                onError: (e) => Toast(e.message, 'error'),
                              })
                            : Async.twoFactorSendEmail(partialToken, (e) =>
                                Toast(e.message, 'error')
                              )
                        );
                        if (!isProfileMode) setEmailSent(true);
                      }}
                    >
                      Send code
                    </SubmitButton>
                  ) : (
                    <>
                      <FormGroup>
                        <Label htmlFor="totp-digit-0">
                          Enter the 6-digit code
                        </Label>
                        <OtpInput
                          value={code}
                          onChange={setCode}
                          disabled={loading}
                        />
                      </FormGroup>
                      <ActionRow data-test="two-factor-email-actions">
                        <SubmitButton
                          large
                          type="submit"
                          disabled={loading || code.length < 6}
                        >
                          {loading ? (
                            <Loader
                              width="20px"
                              borderWidth="2px"
                              text="Verifying..."
                            />
                          ) : (
                            'Enable 2FA'
                          )}
                        </SubmitButton>
                        <Button
                          type="button"
                          large
                          outlined
                          disabled={resendCooldown}
                          onClick={() => {
                            setResendCooldown(true);
                            setTimeout(() => setResendCooldown(false), 120000);
                            dispatch(
                              isProfileMode
                                ? Async.profileSendEmail2FA({
                                    onError: (e) => Toast(e.message, 'error'),
                                  })
                                : Async.twoFactorSendEmail(partialToken, (e) =>
                                    Toast(e.message, 'error')
                                  )
                            );
                          }}
                        >
                          Resend code
                        </Button>
                      </ActionRow>
                    </>
                  )}
                </SetupForm>
              ) : loadingSetup ? (
                <LoadingWrapper>
                  <Loader width="32px" borderWidth="3px" />
                  <Hint>Loading...</Hint>
                </LoadingWrapper>
              ) : setupData ? (
                <>
                  <QRWrapper>
                    <Hint>
                      Scan the QR code with your authenticator app (Google
                      Authenticator, Authy, etc.)
                    </Hint>
                    <div
                      dangerouslySetInnerHTML={{
                        __html: setupData.qr_code_svg,
                      }}
                    />
                  </QRWrapper>
                  {setupData.otp_secret && (
                    <ManualEntry>
                      <Divider>OR</Divider>
                      <Hint>Manually enter the code below.</Hint>
                      <CodeRow>
                        <code>{setupData.otp_secret}</code>
                        <Button
                          type="button"
                          medium
                          outlined
                          onClick={() => copyToClipBoard(setupData.otp_secret)}
                        >
                          <Icon name="copy" />
                          Copy
                        </Button>
                      </CodeRow>
                    </ManualEntry>
                  )}
                  <SetupForm onSubmit={handleSubmit}>
                    <FormGroup>
                      <Label htmlFor="totp-digit-0">
                        Enter the 6-digit code to confirm
                      </Label>
                      <OtpInput
                        value={code}
                        onChange={setCode}
                        disabled={loading}
                      />
                    </FormGroup>
                    <SubmitButton
                      large
                      type="submit"
                      disabled={loading || code.length < 6}
                    >
                      {loading ? (
                        <Loader
                          width="20px"
                          borderWidth="2px"
                          text="Verifying..."
                        />
                      ) : (
                        'Enable 2FA'
                      )}
                    </SubmitButton>
                  </SetupForm>
                </>
              ) : null}
            </Step>
          ) : loginMethod === 'email' ? (
            <SetupForm onSubmit={handleSubmit}>
              <Hint>
                {emailSent
                  ? 'Enter the 6-digit code we emailed you. It expires in 10 minutes.'
                  : 'We will send a 6-digit code to your email.'}
              </Hint>
              {!emailSent ? (
                <SubmitButton
                  large
                  type="button"
                  disabled={loading}
                  onClick={() => {
                    dispatch(
                      Async.twoFactorSendEmail(partialToken, (e) =>
                        Toast(e.message, 'error')
                      )
                    );
                    setEmailSent(true);
                  }}
                >
                  Send code
                </SubmitButton>
              ) : (
                <>
                  <FormGroup>
                    <Label htmlFor="totp-digit-0">Enter the 6-digit code</Label>
                    <OtpInput
                      value={code}
                      onChange={setCode}
                      disabled={loading}
                    />
                  </FormGroup>
                  <MUICheckbox
                    label="Remember this device for 30 days"
                    checked={rememberDevice}
                    onChange={(e) => setRememberDevice(e.target.checked)}
                  />
                  <ActionRow data-test="two-factor-email-actions">
                    <SubmitButton
                      large
                      type="submit"
                      disabled={loading || code.length < 6}
                    >
                      {loading ? (
                        <Loader
                          width="20px"
                          borderWidth="2px"
                          text="Verifying..."
                        />
                      ) : (
                        'Verify'
                      )}
                    </SubmitButton>
                    <Button
                      type="button"
                      large
                      outlined
                      disabled={resendCooldown}
                      onClick={() => {
                        setResendCooldown(true);
                        setTimeout(() => setResendCooldown(false), 120000);
                        dispatch(
                          Async.twoFactorSendEmail(partialToken, (e) =>
                            Toast(e.message, 'error')
                          )
                        );
                      }}
                    >
                      Resend code
                    </Button>
                  </ActionRow>
                </>
              )}
            </SetupForm>
          ) : (
            <form onSubmit={handleSubmit}>
              <Step $gap="16px">
                <Hint>Enter the 6-digit code from your authenticator app.</Hint>
                <FormGroup>
                  <OtpInput
                    value={code}
                    onChange={setCode}
                    disabled={loading}
                  />
                </FormGroup>
                <MUICheckbox
                  label="Remember this device for 30 days"
                  checked={rememberDevice}
                  onChange={(e) => setRememberDevice(e.target.checked)}
                />
                <SubmitButton
                  large
                  type="submit"
                  disabled={loading || code.length < 6}
                >
                  {loading ? (
                    <Loader
                      width="20px"
                      borderWidth="2px"
                      text="Verifying..."
                    />
                  ) : (
                    'Verify'
                  )}
                </SubmitButton>
              </Step>
            </form>
          )}
        </Body>
      </div>
    </Modal>
  );
};

TwoFactorModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  mode: PropTypes.oneOf(['login', 'setup']),
  setupData: PropTypes.object,
  onConfirm: PropTypes.func,
  onSuccess: PropTypes.func,
  zIndex: PropTypes.number,
  methodOptions: PropTypes.arrayOf(PropTypes.oneOf(['totp', 'email'])),
  forcedMethod: PropTypes.oneOf(['totp', 'email']),
};

export default TwoFactorModal;
